import { Inject, Injectable, Injector } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { BaseApi } from "../base-api";
import { ApiResponseDTO } from "../common.types";
import { APP_CONFIGURATION } from "../../../../../core/src/lib/core.di";
import { AppConfig } from "../../../../../core/src/lib/app-config/config.types";
import { AgentTokenInfo } from "./token.types";

@Injectable({ providedIn: "root" })
export class TokenApi extends BaseApi {
  private basePath: string;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly injector: Injector,
    @Inject(APP_CONFIGURATION) readonly appConfig: AppConfig,
  ) {
    super(injector);
    this.basePath = appConfig.apiEndpointUrl;
  }

  getMyAgentToken(): Observable<string | null> {
    return this.httpClient.get<ApiResponseDTO>(`${this.basePath}/token/agent`).pipe(
      map((response) => {
        if (!response.success) {
          super.handleErrorResponse(response);
        }
        return response.results?.token ?? null;
      }),
    );
  }

  /**
   * The only call that returns the token in clear text - it can never be read back afterwards. A blank
   * reveal dialog would be indistinguishable from a usable token, so a response without one has to fail.
   */
  generateMyAgentToken(): Observable<string> {
    return this.httpClient.post<ApiResponseDTO>(`${this.basePath}/token/agent`, {}).pipe(
      map((response) => {
        if (!response.success) {
          super.handleErrorResponse(response);
        }

        const token = response.results?.token;
        if (!token) {
          this.notificationService.error("", this.translateService.instant("notification.error"));
          throw new Error("Generate agent token response carried no token");
        }
        return token;
      }),
    );
  }

  removeMyAgentToken(): Observable<boolean> {
    return this.httpClient.delete<ApiResponseDTO>(`${this.basePath}/token/agent`).pipe(
      map((response) => {
        if (!response.success) {
          super.handleErrorResponse(response);
        }
        return response.success;
      }),
    );
  }

  /** The backend does the filtering by token type, so this is already agent tokens only. */
  getAllAgentTokens(): Observable<Array<AgentTokenInfo>> {
    return this.httpClient.get<ApiResponseDTO>(`${this.basePath}/token/agent/all`).pipe(
      map((response) => {
        if (!response.success) {
          super.handleErrorResponse(response);
        }
        return response.results.tokens;
      }),
    );
  }

  /** Goes by account, because the overview only ever holds the obfuscated token. */
  removeAgentTokenByAccount(accountId: string): Observable<boolean> {
    return this.httpClient.delete<ApiResponseDTO>(`${this.basePath}/token/agent/by-account/${accountId}`).pipe(
      map((response) => {
        if (!response.success) {
          super.handleErrorResponse(response);
        }
        return response.success;
      }),
    );
  }
}
